import {useState, useCallback, useEffect} from 'react';
import {Range, Editor, Text} from 'slate';

import {sanitizeRange} from '../utils/selectionHelpers';

export const useMultiSelection = (editor: any): any => {
  const [selectedRanges, setSelectedRanges] = useState<any[]>([]);

  // Decorate function to highlight selected ranges
  const decorate = useCallback(
    ([node, path]: any) => {
      if (!Text.isText(node)) {
        return [];
      }

      const ranges: any[] = [];

      // Check if any selected range intersects with this node
      selectedRanges.forEach((range) => {
        const safeRange = sanitizeRange(range, editor);
        if (!safeRange) return;

        if (Range.includes(safeRange, path)) {
          try {
            const intersection = Range.intersection(
              safeRange,
              Editor.range(editor, path)
            );
            if (intersection) {
              ranges.push({
                ...intersection,
                multiSelection: true // Marker for LeafRenderer
              });
            }
          } catch (error) {
            // Ignore stale path snapshots during heavy pagination/multi-select updates.
          }
        }
      });

      return ranges;
    },
    [selectedRanges, editor]
  );

  // Helper to add current selection to list
  const addSelection = useCallback(() => {
    if (editor.selection && !Range.isCollapsed(editor.selection)) {
      const safeRange = sanitizeRange(editor.selection, editor);
      if (!safeRange) return;
      setSelectedRanges((prev) => {
        return [...prev, safeRange];
      });
    }
  }, [editor]);

  // Clear selections
  const clearSelections = useCallback(() => {
    setSelectedRanges([]);
  }, []);

  useEffect(() => {
    setSelectedRanges((prev) =>
      prev
        .map((range: any) => sanitizeRange(range, editor))
        .filter((range: any) => Boolean(range))
    );
  }, [editor.children, editor]);

  return {
    selectedRanges,
    setSelectedRanges,
    decorate,
    addSelection,
    clearSelections
  };
};
